import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { shouldSkipApiCalls } from '../utils/routeUtils';

interface AppSettings {
  appName: string;
  email: string;
  darkLogo: string;
  lightLogo: string;
  favicon: string;
  theme: string;
  defaultLanguage: string;
  userId?: number;
}

interface SettingsContextType {
  settings: AppSettings | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
  updateSettings: (updates: Partial<AppSettings>) => void;
}

const defaultSettings: AppSettings = {
  appName: 'RMS Admin',
  email: 'admin@rms.com',
  darkLogo: '',
  lightLogo: '',
  favicon: '',
  theme: 'light',
  defaultLanguage: 'en'
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const updateFavicon = (faviconUrl: string) => {
    if (!faviconUrl) return;

    // Find existing favicon link or create new one
    let faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]') ||
                     document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]');

    if (!faviconLink) {
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      document.head.appendChild(faviconLink);
    }

    faviconLink.href = faviconUrl;
  };

  const updateDocumentTitle = (appName: string) => {
    if (appName) {
      document.title = `${appName} - Admin Panel`;
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token") || "";
      const response = await fetch(
        `${import.meta.env.VITE_API_BASEURL}/settings/general/1`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data && data !== false) {
          const newSettings = {
            appName: data.appName || defaultSettings.appName,
            email: data.email || defaultSettings.email,
            darkLogo: data.darkLogo || defaultSettings.darkLogo,
            lightLogo: data.lightLogo || defaultSettings.lightLogo,
            favicon: data.favicon || defaultSettings.favicon,
            theme: data.theme || defaultSettings.theme,
            defaultLanguage: data.defaultLanguage || defaultSettings.defaultLanguage,
            userId: data.userId,
          };

          setSettings(newSettings);

          // Update dynamic elements
          updateFavicon(newSettings.favicon);
          updateDocumentTitle(newSettings.appName);
        } else {
          setSettings(defaultSettings);
        }
      } else {
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = (updates: Partial<AppSettings>) => {
    if (settings) {
      const newSettings = { ...settings, ...updates };
      setSettings(newSettings);

      // Apply dynamic updates
      if (updates.appName) {
        updateDocumentTitle(updates.appName);
      }

      if (updates.favicon) {
        updateFavicon(updates.favicon);
      }
    }
  };

  useEffect(() => {
    // Skip settings fetch on public pages to avoid unnecessary API calls
    if (shouldSkipApiCalls()) {
      // Set default settings for public pages
      setSettings(defaultSettings);
      setLoading(false);
    } else {
      fetchSettings();
    }
  }, []);

  const refreshSettings = async () => {
    await fetchSettings();
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};